import { contrastRatio, HSLColor } from '@yola/yola-palette-generator';
import { textFormatting } from '@yola/ws-sdk';
import sentry from 'src/js/modules/analytics/sentry';
import parseHslColorString from './parse-hsl-color-string';
import constants from '../constants/custom-color-options';
import parseHslaColorString from './parse-hsla-color-string';
import getSurfaceBackground from './get-surface-background';
import getSurfaceLinkColor from './get-surface-link-color';
import isLightColorType from './is-light-color-type';
import getCompiledCurrentPalette from './get-compiled-current-palette';

const parseColor = (color) =>
  color.includes('hsla') ? parseHslaColorString(color) : parseHslColorString(color);

const reportInvalidAlpha = (rootVariableName, textVariableName, surfaceName, targetColor) => {
  const error = new Error('getCustomTextColor: invalid alpha in stored color');
  sentry.instance.getInstance().captureException(error, (scope) => {
    scope.setExtras({ rootVariableName, textVariableName, surfaceName, targetColor });
    return scope;
  });
};

const isSuitableColor = (targetHSLObject, surfaceHSLObject) => {
  const acceptableCR = isLightColorType(targetHSLObject)
    ? constants.ACCEPTABLE_LIGHT_CR
    : constants.ACCEPTABLE_DARK_CR;
  return contrastRatio(targetHSLObject, surfaceHSLObject) > acceptableCR;
};

const getCustomTextColor = (
  textVariableName,
  surfaceName,
  paletteVariables = getCompiledCurrentPalette().variables
) => {
  const rootVariableName = textFormatting.helpers.getCustomColorFromTextColor(textVariableName);
  const rootCssVariablesMap = textFormatting.helpers.getCssRootVariablesMap();
  const targetColor = rootCssVariablesMap[rootVariableName];
  const whiteColor = `hsl(${paletteVariables.root.ws_color_text_white})`;
  const blackColor = `hsl(${paletteVariables.root.ws_color_text_black})`;

  if (!targetColor) return '';

  const parsedTargetColor = parseColor(targetColor);
  if (!Number.isFinite(parsedTargetColor.a)) {
    // to debug https://yola.sentry.io/issues/7490320513/?project=1334264
    reportInvalidAlpha(rootVariableName, textVariableName, surfaceName, targetColor);
    parsedTargetColor.a = 1;
  }
  const targetHSLObject = new HSLColor(...Object.values(parsedTargetColor));

  const surfaceColor = getSurfaceBackground(surfaceName, paletteVariables);
  const surfaceHSLObject = new HSLColor(...Object.values(parseColor(surfaceColor)));

  const isSuitable = isSuitableColor(targetHSLObject, surfaceHSLObject);
  const cssVariable = `var(${rootVariableName})`;

  if (isSuitable) {
    return cssVariable;
  }

  const { HSLObject, CSSValue } = getSurfaceLinkColor(
    { surfaceName, surfaceColor },
    { targetColor, blackColor, whiteColor }
  );

  // Here we handle case where the color of the surface link is unsuitable due to the contrast ratio
  // This can happen if the user has changed the colors of the palette to a shade that is not recommended (for example: light color to dark)
  // So, if surface link color is not suitable, we apply default color
  const isLinkColorSuitable = isSuitableColor(HSLObject, surfaceHSLObject);

  return isLinkColorSuitable ? CSSValue : cssVariable;
};

export default getCustomTextColor;
